import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { COLORS, ROUTE_PATHS } from '@constants';
import { useStore } from '@hooks';
import { Button, Divider, Loader, Typography } from '@ui';
import { Col, Flex, Layout, Row } from 'antd';
import { observer } from 'mobx-react';
import NextButton from '../components/NextStepButton';
import { TOTAL_STEPS } from '../constants';
import { useCalculatePriority } from '../hooks/useCalculatePriority';
import { useCampaignWizard } from '../hooks/useCampaignWizard';
import { StyledLayout } from './styled';
const { Header } = Layout;

const CampaignWizardLayout = observer(({ children }) => {
    const store = useStore();
    const { getCampaign, resetCampaign, resetPreviewProducts } = store.campaignWizardStore;
    const { isCreate, isEdit, step, campaignId } = useCampaignWizard();

    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    // getCampaign is a createStoreAction flow: it never rejects, it resolves with `{ error }`.
    // Without inspecting that, a failed load leaves `campaign` null and the step renders its
    // own loader forever.
    const loadCampaign = useCallback(async () => {
        setLoading(true);

        const result = campaignId ? await getCampaign(campaignId) : null;

        setLoadError(result?.error ?? null);
        setLoading(false);
    }, [campaignId, getCampaign]);

    // Keyed on campaignId, not mount: this is a layout route, so it does not remount when the
    // wizard moves from step-1 (no id) to step-2/:id and would otherwise never load the campaign
    // it just created.
    useEffect(() => {
        loadCampaign();
    }, [campaignId]);

    useEffect(
        () => () => {
            resetCampaign();
            resetPreviewProducts();
        },
        []
    );

    // Only a campaign this account cannot see is a dead end. Anything else may be transient,
    // so keep the user in the wizard and let them retry rather than ejecting them.
    const loadErrorStatus = loadError?.response?.status;
    const isCampaignUnavailable = loadErrorStatus === 404 || loadErrorStatus === 403;

    const calculatePriority = useCalculatePriority();

    useEffect(() => {
        if (isCreate && step === 2 && !loading && !loadError) {
            calculatePriority();
        }
    }, [step, isCreate, loading, loadError]);

    if (isCampaignUnavailable) {
        return <Navigate to={ROUTE_PATHS.CAMPAIGNS} replace />;
    }

    return (
        <StyledLayout>
            <Header tagName='div' style={{ padding: 0 }}>
                <Row gutter={24} align='middle' justify='space-between'>
                    <Col>
                        <Typography.Text
                            data-e2e={isEdit ? 'campaigns_edit-campaign_title' : 'campaigns_create-campaign_title'}
                            type='em0'
                        >
                            {isEdit ? 'Edit Campaign' : 'Create Campaign'}
                        </Typography.Text>
                    </Col>
                    <Col>
                        <Flex justify='center' align='center' gap={24}>
                            {isCreate && (
                                <Typography.Text data-e2e='campaignwizard_current-step_value' type='badgeMedium'>
                                    Step {step} of {TOTAL_STEPS}
                                </Typography.Text>
                            )}
                            <NextButton />
                        </Flex>
                    </Col>
                </Row>
            </Header>
            <Flex vertical style={{ flexGrow: 1 }}>
                <Divider
                    style={{ borderBlockStart: `1px solid ${COLORS.$gray20Black}`, flexGrow: 0 }}
                    margin='48px 0'
                />
                <div style={{ position: 'relative', flexGrow: 1 }}>
                    {loading ? (
                        <Loader />
                    ) : loadError ? (
                        <Flex vertical align='center' justify='center' gap={16} style={{ height: '100%' }}>
                            <Typography.Text type='body1'>Couldn&apos;t load this campaign.</Typography.Text>
                            <Button type='primary' onClick={loadCampaign}>
                                Try again
                            </Button>
                        </Flex>
                    ) : (
                        children || <Outlet />
                    )}
                </div>
            </Flex>
        </StyledLayout>
    );
});

export default CampaignWizardLayout;
